/**
 * Forgot Password page
 * Allows users to request a password reset email
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { LumiLogo } from "../../shared/components/branding/LumiLogo";

interface ApiResponse {
  message?: string;
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void submitForm();
  };

  const submitForm = async () => {
    setIsSubmitting(true);
    setMessage(null);
    setError(null);

    const apiBase = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";

    try {
      const res = await fetch(`${apiBase}/v1/auth/request-reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (res.ok) {
        const data = (await res.json()) as ApiResponse;
        setMessage(data.message ?? "Password reset email sent");
        setEmail(""); // Clear form
      } else {
        const data = (await res.json()) as ApiResponse;
        setError(data.message ?? "Failed to request password reset");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <LumiLogo className="h-16 w-auto" />
        </div>

        {/* Title */}
        <div className="text-center">
          <h1 className="text-3xl font-bold">Reset your password</h1>
          <p className="mt-2 text-gray-400">Enter your email and we'll send you a reset link</p>
        </div>

        {/* Success message */}
        {message && (
          <div className="p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-400">{message}</div>
        )}

        {/* Error message */}
        {error && <div className="p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400">{error}</div>}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F3B33B]"
              placeholder="you@example.com"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 px-4 bg-[#F3B33B] text-black font-semibold rounded-lg hover:bg-[#F3B33B]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Sending..." : "Send reset link"}
          </button>
        </form>

        {/* Back to sign in */}
        <div className="text-center text-sm text-gray-400">
          Remember your password?{" "}
          <Link to="/sign-in" className="text-[#F3B33B] hover:underline">
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
